/**
 * App.tsx — root React component and authenticated-route gate.
 *
 * Owns: the AuthProvider / TimeTrackingProvider / TwilioProvider /
 *       ToastProvider / NotificationProvider tree, the top app-bar
 *       (theme toggle, search, global metric-form launcher, command
 *       palette), the gate that swaps in `LoginPage` when the user is
 *       unauthenticated, and the import / metric-target dialogs that
 *       hang off the app bar.
 * Used by: `index.tsx` (the React entry point) renders this once.
 * Depends on: `AppShell` (the actual shell + router), every Provider
 *       context, `services/api.ts` for the metrics + users endpoints.
 * Notes: Frozen file (operating-rules.md §4). High blast radius — every
 *       authenticated render passes through this component. Reads
 *       `process.env.REACT_APP_API_URL` indirectly via api.ts only;
 *       direct env reads were intentionally not migrated under 0b-01.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, Routes, Route } from 'react-router-dom';
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Avatar,
  Select,
  FormControl,
  SelectChangeEvent,
  Chip,
  alpha,
} from '@mui/material';
// UploadFileIcon removed (Import button moved)
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import CheckIcon from '@mui/icons-material/Check';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AppShell from './components/AppShell';
import GlobalTimeTracker from './components/timeTracking/GlobalTimeTracker';
import GlobalPhoneDialerButton from './components/phone/dialer/GlobalPhoneDialerButton';
import GlobalAddTodo from './components/todos/GlobalAddTodo';
import ErrorBoundary from './components/ErrorBoundary';
import MetricForm from './components/MetricForm';
import SyncProgressDialog from './components/SyncProgressDialog';
import TargetForm from './components/TargetForm';
import MetricSettingsDialog from './components/MetricSettingsDialog';
import LoginPage from './components/LoginPage';
import KeyboardShortcutsDialog from './components/KeyboardShortcutsDialog';
import CommandPalette from './components/CommandPalette';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TimeTrackingProvider, useTimeTracking } from './contexts/TimeTrackingContext';
import { TwilioProvider } from './contexts/TwilioContext';
import { SmokeRunProvider } from './contexts/SmokeRunContext';
import { ToastProvider } from './contexts/ToastContext';
import { NotificationProvider } from './contexts/NotificationContext';
import UpcomingCallNotification from './components/UpcomingCallNotification';
import { metricsApi, usersApi } from './services/api';
import Snackbar from '@mui/material/Snackbar';
import StopIcon from '@mui/icons-material/Stop';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import EditIcon from '@mui/icons-material/Edit';
import { Metric } from './types';
import DevModeIndicator from './components/debug/DevModeIndicator';
// HomeIcon + KeyboardIcon removed from top bar (moved to Settings)
import { CssBaseline, ThemeProvider, useMediaQuery } from '@mui/material';
import { buildTheme, type ThemeMode } from './theme';
import { applyThemeCssVariables } from './theme/cssVariables';
import { getTimerTaskColor, timerTaskAbbrev, formatDuration } from './utils/timeFormat';
import { NotificationBell, NotificationPopover } from './components/notifications/NotificationCenter';
import CsatThanksPage from './pages/CsatThanksPage';
import { lazy, Suspense } from 'react';

const DemoApp = lazy(() => import('./demo/DemoApp'));
const MobileShell = lazy(() => import('./components/mobile/MobileShell'));
// Documentation Nation live share page (state.md Entry 110) — public
// read-only render of a documentation workflow, token-addressed.
const SharedDocumentationPage = lazy(() => import('./components/engine/documentationNation/SharedDocumentationPage'));
const PublicBookingPage = lazy(() => import('./components/scheduling/PublicBookingPage'));
// Client Portal v1 (state.md Entry 287) — the client login portal SPA
// (CON-020). Replaces the legacy token-viewer ClientPortalPage on the
// same public path; own auth universe, still outside AuthProvider.
const PortalApp = lazy(() => import('./components/portal/PortalApp'));
const BookingManagePage = lazy(() => import('./components/scheduling/BookingManagePage'));
// Wave W Chunk C — new public unauthenticated route for {{csat_link}}.
// Lives alongside /csat-thanks (which is the post-star-click landing).
// Code-split so the main app bundle doesn't pay for it.
const CsatLandingPage = lazy(() => import('./pages/CsatLandingPage'));
// Newsletter v0a-4 — three public unauthenticated surfaces (subscribe,
// double-opt-in confirm, RFC 8058 unsubscribe landing). State.md
// Entry 55 covers this App.tsx unlock (originally drafted as 54;
// renumbered after a parallel Connected Accounts chat took 54). Code-
// split per the existing public-route convention.
const NewsletterSubscribePage = lazy(() => import('./components/newsletter/NewsletterSubscribePage'));
const NewsletterConfirmPage = lazy(() => import('./components/newsletter/NewsletterConfirmPage'));
// One page serves both token families (newsletter + campaign) — see
// components/public/UnsubscribePage. state.md Entry 275.
const UnsubscribePage = lazy(() => import('./components/public/UnsubscribePage'));
// Form Builder Phase 1b — public form-fill surface. State.md Entry 76
// covers the App.tsx unlock for this single route. Code-split per the
// existing public-route convention so the main bundle stays lean.
const PublicFormPage = lazy(() => import('./components/forms/PublicFormPage'));
const PublicRoundApp = lazy(() => import('./components/assessments/public/PublicRoundApp'));

function AppContent() {
  const auth = useAuth();
  const tt = useTimeTracking();
  const navigate = useNavigate();
  const [metricFormOpen, setMetricFormOpen] = useState(false);
  const [defaultMetricDepartment, setDefaultMetricDepartment] = useState<string | undefined>(undefined);
  const [editingMetricId, setEditingMetricId] = useState<number | undefined>(undefined);
  const [targetFormOpen, setTargetFormOpen] = useState(false);
  const [selectedMetricForTarget, setSelectedMetricForTarget] = useState<Metric | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [metricSettingsOpen, setMetricSettingsOpen] = useState(false);
  // R3.2: theme mode is now a three-value enum (light / sepia / dark). Migration:
  //  - Pre-R3.2 stored values were 'dark' or 'light' strings (R3 era) or 'true'/'false'
  //    booleans (pre-R3). All three map cleanly to the new enum on first read.
  //  - If no stored value exists, defer to OS prefers-color-scheme (which only
  //    resolves to light/dark — sepia is always an explicit opt-in).
  const [mode, setMode] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('bsg-theme');
    if (saved === 'light' || saved === 'sepia' || saved === 'dark') return saved;
    if (saved === 'true') return 'dark';
    if (saved === 'false') return 'light';
    return 'dark';
  });
  const [themeMenuAnchor, setThemeMenuAnchor] = useState<null | HTMLElement>(null);
  // R4.4 Part A: named handlers so the menu open/close path is stable and not
  // re-created every render. Inline arrow functions in JSX also worked in
  // principle, but Cowork reported the AppBar Theme button as a no-op on
  // staging — defending against any subtle closure/identity instability.
  const handleThemeMenuOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setThemeMenuAnchor(e.currentTarget);
  }, []);
  const handleThemeMenuClose = useCallback(() => setThemeMenuAnchor(null), []);
  const handleSelectMode = useCallback((m: ThemeMode) => {
    setMode(m);
    setThemeMenuAnchor(null);
  }, []);

  // Home page preference
  // Home page selector moved to Settings > My Profile

  // Command Palette state
  const [cmdPaletteOpen, setCmdPaletteOpen] = useState(false);

  // Keyboard shortcuts modal
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  // Notification Center state
  const [notifAnchor, setNotifAnchor] = useState<null | HTMLElement>(null);
  const [activityLog, setActivityLog] = useState<Array<{ id: string; message: string; time: Date; type: 'sync' | 'info' | 'success' | 'error' }>>([]);

  const [, setRefreshing] = useState(false);
  const [updatingScorecard, setUpdatingScorecard] = useState(false);
  const [updateMenuAnchor, setUpdateMenuAnchor] = useState<null | HTMLElement>(null);
  const [syncJobId, setSyncJobId] = useState<string | null>(null);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);

  // "Preview nav as role…" is a SUPER-ADMIN-only preview — a regular admin must
  // not see or use it (Entry 182). Gate on super admin only.
  //
  // It is a PURE FRONTEND overlay: `impersonateRole` swaps a React state array
  // and sends nothing to the server, so every backend gate still resolves the
  // real user's role. It previews which nav items a role sees; it cannot test
  // whether the API would refuse them. The label says so (Permissions P2) —
  // making it real needs a downgrade-only header plus an audit trail, which is
  // its own ticket.
  const [allRoles, setAllRoles] = useState<Array<{ id: number; name: string }>>([]);
  const realIsSuperAdmin = (() => {
    const rn = auth.realUser?.roleName?.toLowerCase() || '';
    return rn === 'super admin';
  })();

  useEffect(() => {
    if (!realIsSuperAdmin) return;
    usersApi.getRoles()
      .then(roles => {
        if (Array.isArray(roles)) setAllRoles(roles.map((r: any) => ({ id: r.id, name: r.name })));
      })
      .catch((err) => console.error('Failed to fetch roles:', err));
  }, [realIsSuperAdmin]);

  useEffect(() => {
    applyThemeCssVariables(mode);
  }, [mode]);

  // Activity log helper
  const addActivity = useCallback((message: string, type: 'sync' | 'info' | 'success' | 'error' = 'info') => {
    setActivityLog(prev => [{ id: Date.now().toString(), message, time: new Date(), type }, ...prev].slice(0, 50));
  }, []);

  // Keyboard shortcut: Ctrl+K / Cmd+K for command palette
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setCmdPaletteOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Keyboard shortcut: Ctrl+/ for shortcuts modal (safe — won't conflict with typing)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        setShortcutsOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Keyboard shortcut: Alt+T for timer (open quick timer or stop)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.altKey && (e.key === 't' || e.key === 'T') && !['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement).tagName)) {
        e.preventDefault();
        if (tt.activeTimer) {
          tt.stopTimer();
        } else {
          window.dispatchEvent(new CustomEvent('bsg-open-quick-timer'));
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [tt.activeTimer, tt.stopTimer]);

  const isMobile = useMediaQuery('(max-width:767.95px)');
  const prefersColorSchemeDark = useMediaQuery('(prefers-color-scheme: dark)');

  // System theme: only honors OS prefers-color-scheme when the user has no
  // stored preference, and only resolves to light/dark. Sepia is always an
  // explicit user choice (never inferred from OS).
  useEffect(() => {
    const manualOverride = localStorage.getItem('bsg-theme');
    if (!manualOverride) {
      setMode(prefersColorSchemeDark ? 'dark' : 'light');
    }
  }, [prefersColorSchemeDark]);

  const theme = useMemo(() => buildTheme(mode), [mode]);
  const darkMode = mode === 'dark'; // kept as a derived bool for the few legacy consumers below

  const _handleRefresh = async () => {
    try {
      setRefreshing(true);
      addActivity('Refreshing metrics...', 'sync');
      await metricsApi.refresh();
      setRefreshKey((prev) => prev + 1);
      addActivity('Metrics refreshed successfully', 'success');
    } catch (error) {
      addActivity('Metrics refresh failed', 'error');
      console.error('Failed to refresh:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleUpdateScorecard = async (source?: 'aptly', mode?: 'full' | 'incremental') => {
    try {
      setUpdatingScorecard(true);
      setUpdateMenuAnchor(null);
      addActivity(`Scorecard sync started${source ? ` (${source})` : ''}...`, 'sync');

      // Start sync and get jobId for SSE progress tracking
      const API_BASE = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${API_BASE}/metrics/update-scorecard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ source, mode }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error((errData as { error?: string }).error || response.statusText);
      }
      const body = await response.json();
      // 0b-02c: backend wraps in canonical { success: true, data: { jobId, totalSteps } }
      const payload = body?.data ?? body;
      if (payload?.jobId) {
        setSyncJobId(payload.jobId);
        setSyncDialogOpen(true);
      }
    } catch (error: any) {
      console.error('Failed to start sync:', error);
      alert(`Failed to start sync: ${error.message}`);
    } finally {
      setUpdatingScorecard(false);
    }
  };

  // ─── Auth gate ──────────────────────────────────────────────────────
  if (auth.loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {/* F5 (Entry 239): theme-aware loading bg — was hardcoded '#121215',
            which flashed a black "void" on every refresh for light-theme users.
            `background.default` tracks the active mode (matches the F5a html bg). */}
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  if (!auth.authenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoginPage />
      </ThemeProvider>
    );
  }

  if (auth.isPending()) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoginPage pending userName={auth.user?.displayName} />
      </ThemeProvider>
    );
  }

  if (isMobile) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Suspense fallback={
          <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        }>
          <MobileShell mode={mode} onSetMode={setMode} />
        </Suspense>
        <UpcomingCallNotification />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* state.md Entry 308 — the app shell is a FLEX COLUMN pinned to the
          viewport, and <Container> below is the app's ONE scroll port. Before
          this, the shell was a plain `flexGrow:1` Box in normal document flow
          and AppShell's root demanded `minHeight:100vh` while sitting 73px down
          the page (65px AppBar + 8px Container mt) with 32px of Container mb
          under it — so EVERY page was exactly 105px taller than the window and
          scrolled into dead space. Flush surfaces (Email / Help Desk) showed it
          worst: their content is viewport-pinned and never grows to absorb it.
          The column also absorbs the VARIABLE-height in-flow banners below
          (impersonation, dev mode) automatically — which is precisely why no
          hardcoded `calc(100vh - N)` offset can ever be correct here. */}
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <AppBar
          position="static"
          elevation={darkMode ? 0 : 1}
          sx={{
            flexShrink: 0,
            borderBottom: (t) => `1px solid ${t.palette.divider}`,
            ml: 'var(--bsg-sidebar-width, 220px)',
            width: 'calc(100% - var(--bsg-sidebar-width, 220px))',
            transition: 'margin-left 0.2s ease, width 0.2s ease',
          }}
        >
          <Toolbar>
            <Box component="div" sx={{ flexGrow: 1 }} />

            {/* Role impersonation selector (super-admins only — Entry 182) */}
            {realIsSuperAdmin && allRoles.length > 0 && (
              <FormControl size="small" sx={{ ml: 1, minWidth: 140 }}>
                <Select
                  displayEmpty
                  value={auth.impersonation.active ? String(auth.impersonation.roleId) : ''}
                  onChange={(e: SelectChangeEvent<string>) => {
                    const val = e.target.value;
                    if (!val) {
                      auth.stopImpersonating();
                    } else {
                      auth.impersonateRole(Number(val));
                    }
                  }}
                  renderValue={(selected) => {
                    if (!selected) return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', fontSize: '0.78rem' }}>
                        <VisibilityIcon sx={{ fontSize: 14 }} /> Preview nav as role...
                      </Box>
                    );
                    const role = allRoles.find(r => r.id === Number(selected));
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.78rem' }}>
                        <VisibilityIcon sx={{ fontSize: 14, color: 'warning.main' }} /> {role?.name}
                      </Box>
                    );
                  }}
                  sx={{
                    height: 32,
                    fontSize: '0.78rem',
                    '& .MuiSelect-select': { py: 0.5 },
                    bgcolor: auth.impersonation.active ? 'rgba(201,169,97,0.1)' : undefined,
                    border: auth.impersonation.active ? '1px solid rgba(201,169,97,0.4)' : undefined,
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: '12.5px' }}>
                    <em>My real role</em>
                  </MenuItem>
                  {allRoles.map(r => (
                    <MenuItem key={r.id} value={String(r.id)} sx={{ fontSize: '12.5px' }}>
                      {r.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Personal quick actions — time tracker + phone dialer, docked in
                the header console (Mike: "the header is their quick action
                console"; no longer floating/frozen over content). "+ To-do"
                adds a to-do from anywhere, filled in from the page it is
                pressed on (the to-dos upgrade, state.md Entry 467). */}
            <GlobalAddTodo />
            <GlobalTimeTracker />
            <GlobalPhoneDialerButton />


            {/* Command Palette trigger */}
            <Tooltip title="Search & Navigate (Ctrl+K)">
              <IconButton
                aria-label="Search and navigate"
                color="inherit"
                onClick={() => setCmdPaletteOpen(true)}
                sx={{ ml: 0.5, '&:hover': { bgcolor: 'action.hover' } }}
              >
                <SearchIcon />
              </IconButton>
            </Tooltip>

            {/* Notification bell */}
            <NotificationBell onOpen={(e) => setNotifAnchor(e.currentTarget)} />

            {/* R4.4 Part A: Theme mode toggle wrapped in Tooltip for parity with
                the adjacent AI/Search/Notification buttons (which work). Inline
                arrow onClicks replaced with stable useCallback handlers so the
                button identity is invariant across renders. Menu carries
                explicit slotProps z-index above the fixed sidebar (1201) so it
                cannot be obscured by the AppShell sidebar Paper. */}
            <Tooltip title={`Theme: ${mode.charAt(0).toUpperCase()}${mode.slice(1)}`}>
              <IconButton
                aria-label="Theme mode"
                color="inherit"
                onClick={handleThemeMenuOpen}
                sx={{
                  ml: 0.5,
                  transition: 'transform 0.3s',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                {mode === 'dark' ? <DarkModeIcon /> : mode === 'sepia' ? <LocalCafeIcon /> : <LightModeIcon />}
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={themeMenuAnchor}
              open={Boolean(themeMenuAnchor)}
              onClose={handleThemeMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: { mt: 0.5, zIndex: 1400 } } }}
            >
              <MenuItem onClick={() => handleSelectMode('light')}>
                <ListItemIcon><LightModeIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Light</ListItemText>
                {mode === 'light' && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
              </MenuItem>
              <MenuItem onClick={() => handleSelectMode('sepia')}>
                <ListItemIcon><LocalCafeIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Sepia</ListItemText>
                {mode === 'sepia' && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
              </MenuItem>
              <MenuItem onClick={() => handleSelectMode('dark')}>
                <ListItemIcon><DarkModeIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Dark</ListItemText>
                {mode === 'dark' && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
              </MenuItem>
            </Menu>

            {/* User avatar and logout */}
            {auth.user && (
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 1.5, gap: 1 }}>
                <Tooltip title={`${auth.user.displayName} (${auth.user.roleName || 'No role'})`}>
                  <Avatar
                    src={auth.user.avatarUrl}
                    alt={auth.user.displayName}
                    sx={{ width: 32, height: 32, border: '1.5px solid rgba(201,169,97,0.3)' }}
                  />
                </Tooltip>
                {/* CD-12/4A (state.md Entry 255): top-bar sign-out removed —
                    logout now lives only in the HubRail profile block (bottom-left). */}
              </Box>
            )}

          </Toolbar>
        </AppBar>
        {/* Impersonation active banner */}
        {auth.impersonation.active && (
          <Box sx={{
            flexShrink: 0,
            bgcolor: 'rgba(201,169,97,0.15)',
            borderBottom: '1px solid rgba(201,169,97,0.3)',
            px: 2, py: 0.6,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
            ml: 'var(--bsg-sidebar-width, 220px)',
            transition: 'margin-left 0.2s ease',
          }}>
            <VisibilityIcon sx={{ fontSize: 16, color: 'warning.main' }} />
            <Typography variant="body2" sx={{ fontSize: '12.5px', color: 'warning.main', fontWeight: 600 }}>
              Previewing the nav as: {auth.impersonation.roleName}
            </Typography>
            {/* Say what this is NOT. Nothing is sent to the server: req.user,
                the session and every permission gate still resolve YOUR real
                role, so this previews which nav items a role sees and cannot
                test whether the API would refuse them. Left as a preview
                deliberately — a header that changes effective permissions is
                its own security-reviewed ticket (deferred 2026-08-18) — but it
                must not imply it tested the API, because that is exactly the
                false confidence Permissions Phase 1 and 2 exist to remove. */}
            <Typography variant="body2" sx={{ fontSize: '11px', color: 'text.secondary' }}>
              (menu only — the API still uses your own role)
            </Typography>
            {auth.isTeamMember() && (
              <FormControl size="small" sx={{ ml: 1, minWidth: 120 }}>
                <Select
                  displayEmpty
                  value={auth.impersonation.department || ''}
                  onChange={(e: SelectChangeEvent<string>) => {
                    auth.setImpersonationDepartment(e.target.value || null);
                  }}
                  sx={{ height: 26, fontSize: '12.5px', '& .MuiSelect-select': { py: 0.3 } }}
                >
                  <MenuItem value="" sx={{ fontSize: '12.5px' }}><em>All departments</em></MenuItem>
                  <MenuItem value="Operations" sx={{ fontSize: '12.5px' }}>Operations</MenuItem>
                  <MenuItem value="Sales" sx={{ fontSize: '12.5px' }}>Sales</MenuItem>
                  <MenuItem value="Marketing" sx={{ fontSize: '12.5px' }}>Marketing</MenuItem>
                </Select>
              </FormControl>
            )}
            <Button
              size="small"
              onClick={() => auth.stopImpersonating()}
              startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              sx={{ textTransform: 'none', fontSize: '12.5px', ml: 1, color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
            >
              Exit
            </Button>
          </Box>
        )}

        {/* Dev Mode banner (visible when enabled) */}
        <DevModeIndicator />

        {/* Running-timer banner consolidated into the GlobalTimeTracker pill + dropdown (top app-bar) — Tracker Control. */}

        {/* Undo toast: last stopped entry */}
        <Snackbar
          open={!!tt.lastStoppedEntry}
          autoHideDuration={8000}
          onClose={() => tt.dismissLastStopped()}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          message={tt.lastStoppedEntry ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '13.5px' }}>
                Logged <strong>{formatDuration(tt.lastStoppedEntry.durationSeconds)}</strong> on{' '}
                <strong>{tt.lastStoppedEntry.taskName}</strong> — {tt.lastStoppedEntry.projectName}
              </Typography>
            </Box>
          ) : undefined}
          action={
            <Button
              size="small"
              onClick={() => {
                tt.dismissLastStopped();
                navigate('/time-tracking');
              }}
              startIcon={<EditIcon sx={{ fontSize: 14 }} />}
              sx={{ color: '#C9A961', textTransform: 'none', fontSize: '0.78rem' }}
            >
              View
            </Button>
          }
        />

        {/* state.md Entry 308 — THE app scroll port. `flex:1; minHeight:0`
            makes its height DEFINITE (the free space under the app bar and any
            banners), which is what lets in-shell surfaces fill with `flex:1` /
            `height:100%` instead of guessing `calc(100vh - N)`. `mb:4` is gone
            on purpose: under a flex column a bottom margin is permanently
            un-scrollable viewport, i.e. exactly the white band this fixes —
            trailing breathing room now belongs to the surface, not the shell.
            `bgcolor` moves here so the scroll port always paints `--bg` even
            when a page's content is shorter or taller than the port. */}
        <Container
          maxWidth={false}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            bgcolor: 'var(--bg)',
            mt: 1,
            px: 0.5,
            ml: 'var(--bsg-sidebar-width, 220px)',
            width: 'calc(100% - var(--bsg-sidebar-width, 220px))',
            transition: 'margin-left 0.2s ease, width 0.2s ease',
          }}
        >
          <ErrorBoundary fallbackMessage="The main dashboard encountered an error.">
            <AppShell
              refreshSignal={refreshKey}
              onOpenMetricSettings={(metricId) => {
                setEditingMetricId(metricId);
                setMetricSettingsOpen(true);
              }}
              onEditMetric={(metricId) => {
                setEditingMetricId(metricId);
                setMetricFormOpen(true);
              }}
              onAddMetric={() => {
                setEditingMetricId(undefined);
                setDefaultMetricDepartment(undefined);
                setMetricFormOpen(true);
              }}
            />
          </ErrorBoundary>
        <MetricForm
          open={metricFormOpen}
          onClose={() => {
            setMetricFormOpen(false);
            setEditingMetricId(undefined);
            setDefaultMetricDepartment(undefined);
          }}
          onSuccess={() => {
            setRefreshKey((prev) => prev + 1);
            setEditingMetricId(undefined);
            setDefaultMetricDepartment(undefined);
          }}
          metricId={editingMetricId}
          defaultDepartmentName={defaultMetricDepartment}
        />
        <TargetForm
          open={targetFormOpen}
          onClose={() => {
            setTargetFormOpen(false);
            setSelectedMetricForTarget(null);
          }}
          onSuccess={() => {
            setRefreshKey((prev) => prev + 1);
          }}
          metric={selectedMetricForTarget}
        />
        <SyncProgressDialog
          open={syncDialogOpen}
          onClose={() => {
            setSyncDialogOpen(false);
            setSyncJobId(null);
            setRefreshKey((prev) => prev + 1);
          }}
          jobId={syncJobId}
        />
        <MetricSettingsDialog
          open={metricSettingsOpen}
          onClose={() => {
            setMetricSettingsOpen(false);
            setEditingMetricId(undefined);
          }}
          metricId={editingMetricId}
          onOpenTarget={async (metricId) => {
            try {
              const metric = await metricsApi.getById(metricId);
              setSelectedMetricForTarget(metric);
              setTargetFormOpen(true);
            } catch (error) {
              console.error('Failed to load metric for target:', error);
            }
          }}
          onSaved={() => {
            setRefreshKey((prev) => prev + 1);
          }}
        />
        </Container>

        <CommandPalette
          open={cmdPaletteOpen}
          onClose={() => setCmdPaletteOpen(false)}
          mode={mode}
          onSetMode={setMode}
          onSyncAll={() => handleUpdateScorecard()}
        />

        <KeyboardShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

        {/* ── Upcoming Call Notification (global) ── */}
        <UpcomingCallNotification />


        {/* ── Notification / Activity Feed Popover ── */}
        <NotificationPopover
          anchorEl={notifAnchor}
          onClose={() => setNotifAnchor(null)}
          activityLog={activityLog}
          onClearActivity={() => setActivityLog([])}
        />
      </Box>
    </ThemeProvider>
  );
}

// ── Public-route Suspense wrappers ──────────────────────────────────────
// Each public surface preserves its bespoke loading-screen background
// color so the Phase 6.5b migration is byte-identical to the old
// pathname-dispatch behavior (port-not-redesign).
const PublicSpinner: React.FC<{ bg: string; accent?: string }> = ({ bg, accent }) => (
  <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: bg }}>
    <CircularProgress sx={accent ? { color: accent } : undefined} />
  </Box>
);

/** Authenticated-tree wrapper. Wraps every authenticated route's element
 *  in the same provider stack the homegrown dispatcher previously wrapped
 *  the entire app in. Public routes (matched first in the Routes tree)
 *  never instantiate this wrapper — they bypass AuthProvider entirely, so
 *  no `/api/auth/me` fetch fires on portal / book / demo cold-loads. */
const AuthenticatedShell: React.FC = () => (
  <AuthProvider>
    <ToastProvider>
      <NotificationProvider>
        <TimeTrackingProvider>
          <TwilioProvider>
            {/* Smoke Tests: app-wide run state + floating HUD (frozen unlock
                Entry 179). Inside the Router so the HUD can navigate the app. */}
            <SmokeRunProvider>
              <AppContent />
            </SmokeRunProvider>
          </TwilioProvider>
        </TimeTrackingProvider>
      </NotificationProvider>
    </ToastProvider>
  </AuthProvider>
);

function App() {
  // Phase 6.5b c1: declared <Routes> tree replaces the homegrown
  // window.location.pathname.startsWith dispatch that previously decided
  // public-vs-authenticated rendering. Two-subtree shape:
  //   - Public subtree matches first; each route bypasses AuthProvider
  //     entirely (semantic preserved from pre-6.5b behavior).
  //   - Authenticated `/*` catch-all wraps AppContent in the existing
  //     provider stack via the <AuthenticatedShell> helper above.
  //
  // Ordering note: React Router 6+ matches routes by specificity, not
  // declaration order, so the public routes don't NEED to appear first;
  // they're listed first for human readability matching the prior code
  // shape.
  //
  // Trailing-slash semantics preserved from the pre-migration code:
  //   - '/csat/:token' is path-param (was `pathname.startsWith('/csat/')`
  //     with trailing slash to disambiguate from '/csat-thanks').
  //   - '/csat-thanks/*' carries an open wildcard because the prior
  //     check was a bare `startsWith('/csat-thanks')` — any suffix matched.
  //   - '/portal/*', '/booking/:token/*', '/demo/*' carry wildcards for
  //     surfaces that handle their own internal sub-routing.
  //   - '/newsletter/subscribe' is an exact match (no wildcard) because
  //     the prior check was `pathname === '/newsletter/subscribe'`.
  return (
    <Routes>
      {/* Public subtree — no AuthProvider; renders BEFORE the auth wall.
          Public surfaces deliberately bypass /api/auth/me so external
          contacts (CSAT recipients, booking guests, portal visitors,
          newsletter subscribers, demo prospects) don't trigger a 401
          on cold load. */}
      <Route
        path="/csat/:token"
        element={
          <Suspense fallback={<PublicSpinner bg="#f5f5f5" />}>
            <CsatLandingPage />
          </Suspense>
        }
      />
      <Route path="/csat-thanks/*" element={<CsatThanksPage />} />
      <Route
        path="/portal/*"
        element={
          <Suspense fallback={<PublicSpinner bg="#f2f2f2" accent="#0443ef" />}>
            <PortalApp />
          </Suspense>
        }
      />
      <Route
        path="/book/:slug"
        element={
          <Suspense fallback={<PublicSpinner bg="#121215" />}>
            <PublicBookingPage />
          </Suspense>
        }
      />
      <Route
        path="/booking/:token/*"
        element={
          <Suspense fallback={<PublicSpinner bg="#121215" />}>
            <BookingManagePage />
          </Suspense>
        }
      />
      <Route
        path="/newsletter/subscribe"
        element={
          <Suspense fallback={<PublicSpinner bg="#191919" accent="#0443ef" />}>
            <NewsletterSubscribePage />
          </Suspense>
        }
      />
      <Route
        path="/newsletter/confirm/:token"
        element={
          <Suspense fallback={<PublicSpinner bg="#191919" accent="#0443ef" />}>
            <NewsletterConfirmPage />
          </Suspense>
        }
      />
      <Route
        path="/newsletter/unsubscribe/:token"
        element={
          <Suspense fallback={<PublicSpinner bg="#191919" accent="#0443ef" />}>
            <UnsubscribePage family="newsletter" />
          </Suspense>
        }
      />
      {/* Campaign opt-out (state.md Entry 275). GET /api/track/unsubscribe/:id
          now redirects here instead of mutating on load and rendering bare
          inline HTML — a link scanner fetching the footer link used to opt
          people out silently. Tokened per-recipient capability URL, so it takes
          no canonical /<department>/ route, same as its newsletter sibling. */}
      <Route
        path="/unsubscribe/t/:token"
        element={
          <Suspense fallback={<PublicSpinner bg="#191919" accent="#0443ef" />}>
            <UnsubscribePage family="campaign" />
          </Suspense>
        }
      />
      {/* Public form-fill landing. Moved /forms/:slug → /f/:slug (state.md Entry 84)
          so the authed Forms module can own /forms/*. Public route per Entry 76. */}
      <Route
        path="/f/:slug"
        element={
          <Suspense fallback={<PublicSpinner bg="#f2f2f2" accent="#0443ef" />}>
            <PublicFormPage />
          </Suspense>
        }
      />
      {/* Team Assessment (state.md Entry 528): a round's private link /a/:token and the
          owner's report /a/report/:token. Public, token-addressed; the data source is
          the unauthenticated /api/rounds. */}
      <Route
        path="/a/*"
        element={
          <Suspense fallback={<PublicSpinner bg="#f2f2f2" accent="#0443ef" />}>
            <PublicRoundApp />
          </Suspense>
        }
      />
      {/* Documentation Nation live share page (state.md Entry 110).
          Mirrors /f/:slug: public, token-addressed, read-only — the data
          source is the unauthenticated /api/documentation-shared/:token. */}
      <Route
        path="/d/:token"
        element={
          <Suspense fallback={<PublicSpinner bg="#f2f2f2" accent="#0443ef" />}>
            <SharedDocumentationPage />
          </Suspense>
        }
      />
      <Route
        path="/demo/*"
        element={
          <Suspense fallback={<PublicSpinner bg="#121215" />}>
            <DemoApp />
          </Suspense>
        }
      />

      {/* Authenticated catch-all — wraps AppContent in the provider stack.
          Every authenticated surface lives inside AppShell, which AppContent
          mounts. AppShell still owns its own (homegrown for now) sub-routing;
          Phase 6.5b c2 migrates that to declared nested routes. */}
      <Route path="/*" element={<AuthenticatedShell />} />
    </Routes>
  );
}

export default App;
